import React, { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import Swal from 'sweetalert2';
import { useAuth } from '../src/contexts/AuthContext';
import { useTheme } from '../src/contexts/ThemeContext';
import { Link } from 'react-router-dom';

const getFullImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
  return `${apiBase}${url}`;
};

interface NavbarProps {
  onHomeClick: () => void;
  onCartClick: () => void;
  cartCount: number;
  isAuthenticated: boolean;
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onEmpresasClick?: () => void;
  onUsuariosClick?: () => void;
  onRolesClick?: () => void;
  onUsuarioEmpresasClick?: () => void;
  onClientesClick?: () => void;
  onNegociosClick?: () => void;
  onEmpleadosClick?: () => void;
  onProductosClick?: () => void;
  onPedidosAdminClick?: () => void;
  onRepartidorClick?: () => void;
  onDashboardClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({
  onHomeClick,
  onCartClick,
  cartCount,
  isAuthenticated,
  showSearch = true,
  searchQuery = '',
  onSearchChange,
  onEmpresasClick,
  onUsuariosClick,
  onRolesClick,
  onUsuarioEmpresasClick,
  onClientesClick,
  onNegociosClick,
  onEmpleadosClick,
  onProductosClick,
  onPedidosAdminClick,
  onRepartidorClick,
  onDashboardClick
}) => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrClosing, setQrClosing] = useState(false);
  const [qrEntering, setQrEntering] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setConfigOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    setImgError(false);
  }, [user?.imagenUrl]);

  const closeQrModal = () => {
    setQrClosing(true);
    setQrEntering(false);
    setTimeout(() => {
      setQrModalOpen(false);
      setQrClosing(false);
    }, 250);
  };

  useEffect(() => {
    if (qrModalOpen && !qrClosing) {
      setQrEntering(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setQrEntering(true);
        });
      });
    }
  }, [qrModalOpen]);

  useEffect(() => {
    if (qrModalOpen && !qrDataUrl) {
      QRCode.toDataURL('https://delivery.sinred.red', {
        width: 280,
        margin: 2,
        color: { dark: '#1a1614', light: '#ffffff' }
      }).then(setQrDataUrl).catch(console.error);
    }
  }, [qrModalOpen, qrDataUrl]);

  return (
    <>
    <nav className="fixed top-4 left-0 right-0 z-50 px-3 py-4 pointer-events-none">
      <div className="max-w-[1200px] mx-auto flex items-start justify-between pointer-events-none">
        {/* Logo and Search Area */}
        <div className="flex items-center gap-1 pointer-events-auto">
          <div
            onClick={onHomeClick}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div className="w-10 h-10 rounded-[1rem] bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center shadow-lg shadow-primary/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
              <span className="material-symbols-outlined text-white text-2xl">restaurant</span>
            </div>
          </div>

          {showSearch && (
            <div className=" sm:flex items-center bg-white/10 dark:bg-[#1a1614]/30 backdrop-blur-sm rounded-2xl border border-white/40 dark:border-white/5 px-4 py-2.5 w-50 shadow-premium focus-within:shadow-premium-hover transition-all duration-300 group">
              <span className="hidden sm:block material-symbols-outlined text-gray-400 group-focus-within:text-primary transition-colors">search</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                placeholder="¿Qué se te antoja?"
                className="bg-transparent border-none focus:ring-0 text-sm font-bold w-full text-secondary dark:text-white placeholder:text-gray-400"
              />
            </div>
          )}
        </div>

        {/* Right: Cart + Hamburger */}
        <div ref={menuRef} className="relative pointer-events-auto">
          <div className="flex items-center gap-2">
            {/* Pedidos — visible when authenticated */}
            {isAuthenticated && (
              <button
                onClick={() => { onPedidosAdminClick?.(); }}
                className="relative p-2.5 rounded-xl bg-gray-500/40 dark:bg-[#1a1614]/90 backdrop-blur-sm border border-white/40 dark:border-white/5 hover:bg-white/20 dark:hover:bg-white/10 transition-colors group shadow-premium"
                title="Pedidos"
              >
                <span className="material-symbols-outlined text-gray-100 dark:text-gray-200 group-hover:text-primary transition-colors">local_shipping</span>
              </button>
            )}

            {/* Mis Entregas — visible when authenticated */}
            {isAuthenticated && (
              <button
                onClick={() => { onRepartidorClick?.(); }}
                className="relative p-2.5 rounded-xl bg-gray-500/40 dark:bg-[#1a1614]/90 backdrop-blur-sm border border-white/40 dark:border-white/5 hover:bg-white/20 dark:hover:bg-white/10 transition-colors group shadow-premium"
                title="Mis Entregas"
              >
                <span className="material-symbols-outlined text-gray-100 dark:text-gray-200 group-hover:text-primary transition-colors">two_wheeler</span>
              </button>
            )}

            {/* Carrito — always visible */}
            <button
              onClick={onCartClick}
              className="relative p-2.5 rounded-xl bg-gray-500/40 dark:bg-[#1a1614]/90 backdrop-blur-sm border border-white/40 dark:border-white/5 hover:bg-white/20 dark:hover:bg-white/10 transition-colors group shadow-premium"
            >
              <span className="material-symbols-outlined text-gray-100 dark:text-gray-200 group-hover:text-primary transition-colors">shopping_cart</span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-lg ring-2 ring-white dark:ring-background-dark scale-110">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Hamburger Button */}
            <button
              onClick={() => setMenuOpen(prev => !prev)}
              className="relative w-10 h-10 rounded-xl bg-gray-500/40 dark:bg-[#1a1614]/90 backdrop-blur-sm border border-white/40 dark:border-white/5 hover:bg-white/20 dark:hover:bg-white/10 transition-all group shadow-premium flex items-center justify-center"
            >
              <div className="flex flex-col items-center justify-center gap-[5px] w-5">
                <span className={`block h-[2px] w-full bg-white dark:bg-white rounded-full transition-all duration-300 origin-center ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}></span>
                <span className={`block h-[2px] w-full bg-white dark:bg-white rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}></span>
                <span className={`block h-[2px] w-full bg-white dark:bg-white rounded-full transition-all duration-300 origin-center ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}></span>
              </div>
            </button>
          </div>

          {/* Dropdown Menu */}
          <div className={`absolute top-full right-0 mt-3 w-56 transition-all duration-300 ease-out origin-top-right ${
            menuOpen
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
          }`}>
            <div className="bg-gray-900/40 dark:bg-[#1a1614]/40 backdrop-blur-sm rounded-2xl border border-white/40 dark:border-white/10 shadow-premium-hover overflow-y-auto overflow-x-hidden max-h-[70vh] sm:max-h-[75vh]">
              {/* Theme Toggle */}
              <button
                onClick={() => { toggleTheme(); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group"
              >
                <span className="material-symbols-outlined text-gray-200 dark:text-gray-300 group-hover:text-primary transition-colors">
                  {theme === 'light' ? 'dark_mode' : 'light_mode'}
                </span>
                <span className="text-sm  text-gray-100 dark:text-white group-hover:text-primary transition-colors">
                  {theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
                </span>
              </button>

              <div className="h-px bg-gray-200/60 dark:bg-white/5 mx-4"></div>

              <div>
                <button
                  onClick={() => setConfigOpen(prev => !prev)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-gray-200 dark:text-gray-300 group-hover:text-primary transition-colors">settings</span>
                    <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Configuración</span>
                  </div>
                  <span className={`material-symbols-outlined text-gray-200 dark:text-gray-300 group-hover:text-primary transition-all duration-300 ${configOpen ? 'rotate-180' : ''}`}>expand_more</span>
                </button>

                <div className={`overflow-hidden transition-all duration-300 ${configOpen ? 'max-h-[640px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-3 pb-3 space-y-1">
                    {user?.nombreRol === 'SuperAdmin' && (
                      <button 
                        onClick={() => { onEmpresasClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                      >
                        <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">apartment</span>
                        <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Empresas</span>
                      </button>
                    )}
                    {isAuthenticated && (
                      <>
                        <button 
                          onClick={() => { onDashboardClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">dashboard</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Dashboard</span>
                        </button>
                        <button 
                          onClick={() => { onNegociosClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">domain</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Negocios</span>
                        </button>
                                                <button 
                          onClick={() => { onProductosClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">inventory_2</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Productos</span>
                        </button>
                        <button 
                          onClick={() => { onUsuariosClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">group</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Usuarios</span>
                        </button>
                        <button 
                          onClick={() => { onUsuarioEmpresasClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">groups</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Usuarios-Rol</span>
                        </button>
                        <button 
                          onClick={() => { onEmpleadosClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">assignment_ind</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Empleados</span>
                        </button>
                        <button 
                          onClick={() => { onPedidosAdminClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">local_shipping</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Pedidos</span>
                        </button>
                        <button 
                          onClick={() => { onRepartidorClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">two_wheeler</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Mis Entregas</span>
                        </button>
                        <button 
                          onClick={() => { onClientesClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">badge</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Clientes</span>
                        </button>
                        <button 
                          onClick={() => { onRolesClick?.(); setMenuOpen(false); setConfigOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group text-left"
                        >
                          <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">shield</span>
                          <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Roles</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="h-px bg-gray-200/60 dark:bg-white/5 mx-4"></div>

              {/* Auth Section */}
              {isAuthenticated ? (
                <>
                  <div className="px-5 py-4 flex items-center gap-3">
                    {user?.imagenUrl && !imgError ? (
                      <img 
                        src={getFullImageUrl(user.imagenUrl)!} 
                        alt={user.name} 
                        className="w-9 h-9 rounded-xl object-cover border border-primary/20 shrink-0"
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-black text-sm shrink-0">
                        {user?.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className=" font-black text-white dark:text-white truncate capitalize">{user?.name}</p>
                      <p className="text-[13px] text-gray-400 truncate">{user?.phone}</p>
                      {user?.nombreRol && (
                        <p className="text-[13px] text-primary truncate font-bold">{user.nombreRol}</p>
                      )}
                    </div>
                  </div>

                  <div className="h-px bg-gray-200/60 dark:bg-white/5 mx-4"></div>

                  <button
                    onClick={() => { setQrModalOpen(true); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group"
                  >
                    <span className="material-symbols-outlined text-gray-200 dark:text-gray-300 group-hover:text-primary transition-colors">qr_code_2</span>
                    <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Ver QR</span>
                  </button>

                  {user?.nombreRol === 'SuperAdmin' && (
                    <>
                      <div className="h-px bg-gray-200/60 dark:bg-white/5 mx-4"></div>
                      <Link
                        to="/register"
                        onClick={() => setMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group"
                      >
                        <span className="material-symbols-outlined text-gray-200 dark:text-gray-300 group-hover:text-primary transition-colors">person_add</span>
                        <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Registrarse</span>
                      </Link>
                    </>
                  )}

                  <div className="h-px bg-gray-200/60 dark:bg-white/5 mx-4"></div>

                  <button
                    onClick={async () => {
                      const res = await Swal.fire({
                        title: '¿Cerrar sesión?',
                        text: 'Se cerrará tu sesión actual.',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#d33',
                        cancelButtonColor: '#64748b',
                        confirmButtonText: 'Sí, cerrar',
                        cancelButtonText: 'Cancelar',
                        background: 'rgba(15,23,42,0.9)',
                        color: '#fff',
                        backdrop: false,
                        didOpen: (popup) => {
                          popup.style.borderRadius = '2rem';
                        },
                      });
                      if (!res.isConfirmed) return;
                      logout();
                      setMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-5 py-4 hover:bg-red-50/50 dark:hover:bg-red-500/5 transition-colors group"
                  >
                    <span className="material-symbols-outlined text-gray-100 group-hover:text-red-500 transition-colors">logout</span>
                    <span className="text-sm text-white group-hover:text-red-500 transition-colors">Cerrar Sesión</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMenuOpen(false)}
                    className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group"
                  >
                    <span className="material-symbols-outlined text-gray-300 dark:text-gray-300 group-hover:text-primary transition-colors">login</span>
                    <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Iniciar Sesión</span>
                  </Link>

                  <div className="h-px bg-gray-200/60 dark:bg-white/5 mx-4"></div>

                  <button
                    onClick={() => { setQrModalOpen(true); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group"
                  >
                    <span className="material-symbols-outlined text-gray-200 dark:text-gray-300 group-hover:text-primary transition-colors">qr_code_2</span>
                    <span className="text-sm text-gray-100 dark:text-white group-hover:text-primary transition-colors">Ver QR</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>

      {/* QR Modal */}
      {qrModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeQrModal}
          />
          <div 
            className={`relative bg-white dark:bg-[#1a1614] rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden transform-gpu origin-center ${qrClosing ? 'animate-zoom-out' : qrEntering ? 'animate-zoom-in' : ''}`}
            style={!qrClosing && !qrEntering ? { opacity: 0, transform: 'scale(0.01)' } : undefined}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary">qr_code_2</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">Código QR</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">delivery.sinred.red</p>
                </div>
              </div>
              <button
                onClick={closeQrModal}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
              </button>
            </div>

            {/* QR Code */}
            <div className="p-6 flex flex-col items-center">
              <div className="bg-white p-4 rounded-2xl shadow-lg">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR Code" className="w-64 h-64" />
                ) : (
                  <div className="w-64 h-64 flex items-center justify-center">
                    <span className="material-symbols-outlined text-4xl text-gray-300 animate-pulse">qr_code_2</span>
                  </div>
                )}
              </div>
              <p className="text-center text-sm text-gray-600 dark:text-gray-300 mt-4 font-medium">
                https://delivery.sinred.red
              </p>
              <p className="text-center text-xs text-gray-400 mt-2">
                Escanea este código para acceder
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
